/**
 * WorkspaceDomainsPage - Configuration des domaines autorisés par workspace.
 * 
 * Features:
 * - Liste des domaines autorisés
 * - Ajout de domaines (exact ou wildcard *.example.com)
 * - Toggle actif/inactif
 * - Suppression de domaines
 * - Vérification plan pour autoriser la feature
 * 
 * @security Vérification feature "domain_allowlist" dans plan
 * 
 * TODO: Intégrer dans les settings workspace quand la page sera créée
 * Pour l'instant, cette page est standalone et nécessite workspace_id en props ou URL
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { WorkspaceDomain, WorkspaceDomainCreate } from '../types/workspace'
import { CheckCircle, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react'

export default function WorkspaceDomainsPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const [domains, setDomains] = useState<WorkspaceDomain[]>([])
  const [loading, setLoading] = useState(true)
  const [newDomain, setNewDomain] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [canUseDomainAllowlist, setCanUseDomainAllowlist] = useState(false)
  const [verifyingDns, setVerifyingDns] = useState<number | null>(null)
  const [issuingSsl, setIssuingSsl] = useState<number | null>(null)
  const [configuringNginx, setConfiguringNginx] = useState<number | null>(null)

  const fetchDomains = useCallback(async () => {
    if (!workspaceId) return
    
    setLoading(true)
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/domains`)
      setDomains(data)
      
      // TODO: Vérifier si le plan permet la domain allowlist
      // Pour l'instant, on suppose que c'est disponible
      setCanUseDomainAllowlist(true) // Temporaire - à remplacer par vérification réelle
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setCanUseDomainAllowlist(false)
      } else {
        setError(err?.response?.data?.detail || 'Erreur lors du chargement')
      }
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    fetchDomains()
  }, [fetchDomains])

  const handleAddDomain = async () => {
    if (!workspaceId || !newDomain.trim()) return
    
    setAdding(true)
    setError(null)
    
    try {
      const payload: WorkspaceDomainCreate = { domain: newDomain.trim() }
      const { data } = await api.post(`/workspaces/${workspaceId}/domains`, payload)
      setDomains(prev => [data, ...prev])
      setNewDomain('')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de l\'ajout')
    } finally {
      setAdding(false)
    }
  }

  const handleDeleteDomain = async (domainId: number) => {
    if (!workspaceId) return
    
    try {
      await api.delete(`/workspaces/${workspaceId}/domains/${domainId}`)
      setDomains(prev => prev.filter(d => d.id !== domainId))
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la suppression')
    }
  }

  const handleToggleDomain = async (domainId: number) => {
    if (!workspaceId) return
    
    try {
      const { data } = await api.patch(`/workspaces/${workspaceId}/domains/${domainId}/toggle`)
      setDomains(prev => prev.map(d => d.id === domainId ? data : d))
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la mise à jour')
    }
  }

  const handleVerifyDns = async (domainId: number) => {
    if (!workspaceId) return
    setVerifyingDns(domainId)
    setError(null)
    
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/domains/${domainId}/verify-dns`)
      setDomains(prev => prev.map(d => d.id === domainId ? data : d))
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la vérification DNS')
    } finally {
      setVerifyingDns(null)
    }
  }

  const handleIssueSsl = async (domainId: number) => {
    if (!workspaceId) return
    setIssuingSsl(domainId)
    setError(null)
    
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/domains/${domainId}/issue-ssl`)
      setDomains(prev => prev.map(d => d.id === domainId ? data : d))
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de l\'émission SSL')
    } finally {
      setIssuingSsl(null)
    }
  }

  const handleConfigureNginx = async (domainId: number) => {
    if (!workspaceId) return
    setConfiguringNginx(domainId)
    setError(null)
    
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/domains/${domainId}/configure-nginx`)
      setDomains(prev => prev.map(d => d.id === domainId ? data : d))
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la configuration Nginx')
    } finally {
      setConfiguringNginx(null)
    }
  }

  const getDnsStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium"><CheckCircle className="w-3 h-3" /> Vérifié</span>
      case 'failed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium"><XCircle className="w-3 h-3" /> Échec</span>
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium"><Clock className="w-3 h-3" /> En attente</span>
    }
  }

  const getSslStatusBadge = (status: string) => {
    switch (status) {
      case 'issued':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium"><CheckCircle className="w-3 h-3" /> Émis</span>
      case 'failed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium"><XCircle className="w-3 h-3" /> Échec</span>
      case 'expired':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-medium"><AlertCircle className="w-3 h-3" /> Expiré</span>
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium"><Clock className="w-3 h-3" /> En attente</span>
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">Chargement...</div>
      </div>
    )
  }

  if (!canUseDomainAllowlist) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6">
          <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
            🔒 Fonctionnalité Premium
          </h2>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            La configuration des domaines autorisés nécessite un plan payant.
            Passez à un plan supérieur pour activer cette fonctionnalité.
          </p>
          <a
            href="/pricing"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium transition-colors"
          >
            Voir les plans
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          Domaines Autorisés
        </h1>
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
          Configurez les domaines autorisés à utiliser votre widget embed.
          Si aucun domaine n'est configuré, tous les domaines sont autorisés.
        </p>
      </div>

      {/* Formulaire ajout */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <label htmlFor="new-domain" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
          Ajouter un domaine
        </label>
        <div className="flex gap-2">
          <input
            id="new-domain"
            type="text"
            placeholder="example.com ou *.example.com"
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddDomain()}
            className="flex-1 px-3 py-2 bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={handleAddDomain}
            disabled={adding || !newDomain.trim()}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {adding ? 'Ajout...' : 'Ajouter'}
          </button>
        </div>
        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          Exemples : <code className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">example.com</code>, <code className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">*.example.com</code> (wildcard)
        </p>
      </div>

      {/* Liste domaines */}
      <div className="space-y-4">
        {domains.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
              Aucun domaine configuré. Tous les domaines sont autorisés.
            </p>
          </div>
        ) : (
          domains.map(domain => (
            <div key={domain.id} className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <svg className="h-5 w-5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">{domain.domain}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      Ajouté le {new Date(domain.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={domain.is_active}
                      onChange={() => handleToggleDomain(domain.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-emerald-600"></div>
                  </label>
                  <button
                    onClick={() => handleDeleteDomain(domain.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Phase 3 Agency: DNS/SSL Status */}
              <div className="grid grid-cols-3 gap-4 mb-3 p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
                <div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">DNS</p>
                  {getDnsStatusBadge(domain.dns_status)}
                  {domain.dns_error_message && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{domain.dns_error_message}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">SSL</p>
                  {getSslStatusBadge(domain.ssl_status)}
                  {domain.ssl_expires_at && (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                      Expire le {new Date(domain.ssl_expires_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-1">Nginx</p>
                  {domain.nginx_configured ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                      <CheckCircle className="w-3 h-3" /> Configuré
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-400 text-xs font-medium">
                      <Clock className="w-3 h-3" /> Non configuré
                    </span>
                  )}
                </div>
              </div>

              {/* Phase 3 Agency: Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleVerifyDns(domain.id)}
                  disabled={verifyingDns === domain.id}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {verifyingDns === domain.id ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Vérification...</>
                  ) : (
                    <>Vérifier DNS</>
                  )}
                </button>
                
                <button
                  onClick={() => handleIssueSsl(domain.id)}
                  disabled={domain.dns_status !== 'verified' || issuingSsl === domain.id}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={domain.dns_status !== 'verified' ? 'DNS doit être vérifié d\'abord' : ''}
                >
                  {issuingSsl === domain.id ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Émission...</>
                  ) : (
                    <>Émettre SSL</>
                  )}
                </button>
                
                <button
                  onClick={() => handleConfigureNginx(domain.id)}
                  disabled={domain.ssl_status !== 'issued' || configuringNginx === domain.id}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={domain.ssl_status !== 'issued' ? 'SSL doit être émis d\'abord' : ''}
                >
                  {configuringNginx === domain.id ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Configuration...</>
                  ) : (
                    <>Configurer Nginx</>
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  )
}
